import { Group, Stack, Text } from '@mantine/core';
import type { CapacityResponse } from './types';

/**
 * Exactly the fields this component reads — not the whole `CapacityResponse`.
 *
 * The member portal receives a deliberately narrower payload
 * (`PortalCapacityResponse`, which omits the upgrade ladder and the internal
 * provider names), and this component is shared by both surfaces. Requiring the
 * full response here would force the portal to supply fields it is not sent.
 *
 * Stating the minimum also makes the constraint enforceable: adding a read of a
 * field the portal does not get will fail typecheck here, rather than at the one
 * call site that happens to notice.
 */
export type CapacitySummaryData = Pick<
  CapacityResponse,
  | 'busiest_hour_local'
  | 'dominant_direction'
  | 'measured_buckets'
  | 'plan_download_mbps'
  | 'plan_upload_mbps'
  | 'saturated_share'
  | 'saturated_share_down'
  | 'saturated_share_up'
  | 'site_time_zone'
>;

export interface CapacitySummaryProps {
  data: CapacitySummaryData;
}

const formatHour = (hour: number) =>
  `${String(hour).padStart(2, '0')}:00–${String((hour + 1) % 24).padStart(2, '0')}:00`;

const Stat = ({ label, value }: { label: string; value: string | string[] }) => (
  <Stack gap={2}>
    <Text size="xs" c="dimmed" tt="uppercase">
      {label}
    </Text>
    {(Array.isArray(value) ? value : [value]).map((line) => (
      <Text key={line} size="sm" fw={600}>
        {line}
      </Text>
    ))}
  </Stack>
);

const pct = (fraction: number) => `${(fraction * 100).toFixed(1)}%`;

/** plan · saturated share · per-direction shares · busiest hour · direction.
 *  Data as props only. */
export const CapacitySummary = ({ data }: CapacitySummaryProps) => {
  // saturated_share comes from the backend rather than being recomputed here:
  // the ranking rows already carried it, and two implementations of one ratio
  // is how the ranking page and this page start disagreeing. measured_buckets
  // is still read directly — it is a count, not a derivation.
  const share =
    data.measured_buckets > 0
      ? `${pct(data.saturated_share)} of ${data.measured_buckets} measured minutes`
      : 'No measured minutes yet';
  // Both directions, always together. `saturated_share` is "either direction",
  // so on its own it reads as a download figure and an upload-bound tenant
  // looks like a download-bound one. Served by the backend (same denominator
  // as `saturated_share`); absent on the member-portal payload, where the
  // stat is simply not shown rather than half-shown.
  const byDirection =
    data.measured_buckets > 0 && data.saturated_share_down != null && data.saturated_share_up != null
      ? [
          `Download: at the cap ${pct(data.saturated_share_down)} of busy minutes`,
          `Upload: at the cap ${pct(data.saturated_share_up)} of busy minutes`,
        ]
      : null;
  const plan =
    data.plan_download_mbps != null
      ? `${data.plan_download_mbps} / ${data.plan_upload_mbps} Mbit/s`
      : 'No plan set';
  const busiest =
    data.busiest_hour_local != null
      ? `${formatHour(data.busiest_hour_local)} (${data.site_time_zone})`
      : '—';
  const direction =
    data.dominant_direction === 'down'
      ? 'Downloads'
      : data.dominant_direction === 'up'
        ? 'Uploads'
        : '—';
  return (
    <Group gap="xl" wrap="wrap">
      <Stat label="Plan" value={plan} />
      <Stat label="Saturated" value={share} />
      {byDirection && <Stat label="By direction" value={byDirection} />}
      <Stat label="Busiest hour" value={busiest} />
      <Stat label="Direction" value={direction} />
    </Group>
  );
};
